import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { propertyApi } from '../services';
import './AdminPage.css';

interface ConditionConfigItem {
  id: string;
  value: number;
  localization: string;
}

interface ConditionClassificationConfig {
  weights: {
    features: ConditionConfigItem[];
    ratings: ConditionConfigItem[];
  };
  staticValues?: {
    features?: ConditionConfigItem[];
  };
  thresholds: {
    low: number;
    high: number;
  };
}

const GEW_TYPES = [
  { id: 'wohnung-bestand', label: 'Wohnung Bestand' },
  { id: 'wohnung-gesamt', label: 'Wohnung Gesamt' },
  { id: 'wohnung-neubau', label: 'Wohnung Neubau' },
];

const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedType, setSelectedType] = useState(GEW_TYPES[0].id);
  const [config, setConfig] = useState<ConditionClassificationConfig | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Local Market Data Uploader states
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setUploadError(null);
      setUploadSuccess(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
  });

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    try {
      const result = await propertyApi.uploadLocalMarketData(file);
      setUploadSuccess(result.message || 'Market data imported successfully and properties re-evaluated.');
      setFile(null);
    } catch (err: any) {
      setUploadError(err.message || 'Failed to upload local market data');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.get(
        `/properties/condition-classification/config?gewType=${selectedType}`,
      );
      // Ensure staticValues exists
      const data = response.data.data;
      if (!data.staticValues) data.staticValues = {};
      if (!data.staticValues.features) data.staticValues.features = [];
      setConfig(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch configuration');
    } finally {
      setLoading(false);
    }
  }, [selectedType]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.put(
        `/properties/condition-classification/config?gewType=${selectedType}`,
        config,
      );
      setSuccess('Configuration saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleThresholdChange = (key: 'low' | 'high', value: string) => {
    if (!config) return;
    setConfig({
      ...config,
      thresholds: {
        ...config.thresholds,
        [key]: Number(value),
      },
    });
  };

  const handleItemChange = (
    section: 'weights' | 'staticValues',
    list: 'features' | 'ratings',
    index: number,
    field: keyof ConditionConfigItem,
    value: string | number,
  ) => {
    if (!config) return;
    const sectionData = (config[section] || {}) as any;
    const currentList = (sectionData[list] || []) as ConditionConfigItem[];
    const newList = [...currentList];
    newList[index] = { ...newList[index], [field]: value };
    setConfig({
      ...config,
      [section]: {
        ...sectionData,
        [list]: newList,
      },
    });
  };

  const addItem = (
    section: 'weights' | 'staticValues',
    list: 'features' | 'ratings',
  ) => {
    if (!config) return;
    const sectionData = (config[section] || {}) as any;
    const currentList = (sectionData[list] || []) as ConditionConfigItem[];
    const newList = [...currentList, { id: '', value: 0, localization: '' }];
    setConfig({
      ...config,
      [section]: {
        ...sectionData,
        [list]: newList,
      },
    });
  };

  const removeItem = (
    section: 'weights' | 'staticValues',
    list: 'features' | 'ratings',
    index: number,
  ) => {
    if (!config) return;
    const sectionData = (config[section] || {}) as any;
    const currentList = (sectionData[list] || []) as ConditionConfigItem[];
    const newList = [...currentList];
    newList.splice(index, 1);
    setConfig({
      ...config,
      [section]: {
        ...sectionData,
        [list]: newList,
      },
    });
  };

  const renderList = (
    title: string,
    section: 'weights' | 'staticValues',
    list: 'features' | 'ratings',
  ) => {
    const sectionData = config?.[section] as any;
    const items = (sectionData?.[list] || []) as ConditionConfigItem[];
    return (
      <div className="config-section">
        <h3>
          {title}
          <button className="btn-add" onClick={() => addItem(section, list)}>
            + Add Item
          </button>
        </h3>

        {items.length > 0 && (
          <div className="config-list-header">
            <div>ID</div>
            <div>Value</div>
            <div>Localization Label</div>
            <div></div>
          </div>
        )}

        <div className="config-list">
          {items.map((item, index) => (
            <div key={index} className="config-list-item">
              <input
                type="text"
                value={item.id}
                onChange={(e) =>
                  handleItemChange(section, list, index, 'id', e.target.value)
                }
                placeholder="e.g. conditionBathroom"
              />
              <input
                type="number"
                value={item.value}
                onChange={(e) =>
                  handleItemChange(
                    section,
                    list,
                    index,
                    'value',
                    Number(e.target.value),
                  )
                }
              />
              <input
                type="text"
                value={item.localization}
                onChange={(e) =>
                  handleItemChange(
                    section,
                    list,
                    index,
                    'localization',
                    e.target.value,
                  )
                }
                placeholder="e.g. condition.bathroom"
              />
              <button
                className="btn-icon"
                onClick={() => removeItem(section, list, index)}
                title="Remove Item"
              >
                ✕
              </button>
            </div>
          ))}
          {items.length === 0 && (
            <p style={{ color: 'var(--color-text-muted)' }}>
              No items configured.
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-page">
      <header className="admin-header">
        <h2>Admin Panel - Configuration</h2>
        <div className="admin-info">
          <span>Admin: {user?.email}</span>
        </div>
      </header>

      <main className="admin-content">
        <div className="config-group">
          <details className="config-details">
            <summary className="config-details-summary">
              <div className="header-text-container">
                <h3>Property Condition Classification Configuration</h3>
                <p className="config-group-description">
                  Configure the score thresholds and weights used to compute
                  property condition classifications.
                </p>
              </div>
              <span className="expand-indicator">▼</span>
            </summary>

            <div className="config-details-content">
              <div className="config-selector" ref={dropdownRef}>
                <label>
                  <strong>Property Type:</strong>
                </label>
                <div className="glass-select">
                  <button
                    type="button"
                    className="glass-select-trigger"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    <span>
                      {GEW_TYPES.find((t) => t.id === selectedType)?.label}
                    </span>
                    <span
                      className={`select-arrow ${dropdownOpen ? 'open' : ''}`}
                    >
                      ▼
                    </span>
                  </button>
                  {dropdownOpen && (
                    <div className="glass-select-dropdown">
                      {GEW_TYPES.map((t) => (
                        <div
                          key={t.id}
                          className={`glass-select-option ${
                            selectedType === t.id ? 'active' : ''
                          }`}
                          onClick={() => {
                            setSelectedType(t.id);
                            setDropdownOpen(false);
                          }}
                        >
                          {t.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {error && <div className="status-message error">{error}</div>}
              {success && (
                <div className="status-message success">{success}</div>
              )}

              {loading ? (
                <div className="loading-container">
                  Loading configuration...
                </div>
              ) : config ? (
                <div className="config-editor">
                  <div className="config-section">
                    <h3>Klassen Grenzen (mäßig, gut, sehr gut)</h3>
                    <div className="thresholds-grid">
                      <div className="input-group">
                        <label>Low Threshold (Mäßig ➔ Gut)</label>
                        <div className="input-with-suffix">
                          <input
                            type="number"
                            value={config.thresholds.low}
                            onChange={(e) =>
                              handleThresholdChange('low', e.target.value)
                            }
                          />
                          <span className="input-suffix">%</span>
                        </div>
                      </div>
                      <div className="input-group">
                        <label>High Threshold (Gut ➔ Sehr Gut)</label>
                        <div className="input-with-suffix">
                          <input
                            type="number"
                            value={config.thresholds.high}
                            onChange={(e) =>
                              handleThresholdChange('high', e.target.value)
                            }
                          />
                          <span className="input-suffix">%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {renderList(
                    'Feature Gewichtung (Ja / Nein fragen)',
                    'weights',
                    'features',
                  )}
                  {renderList(
                    'Feature Bewertungen (1-5 Punkte)',
                    'weights',
                    'ratings',
                  )}
                  {renderList(
                    'Addons (addierte Fixwerte)',
                    'staticValues',
                    'features',
                  )}

                  <div className="config-actions">
                    <button
                      className="btn-save"
                      onClick={() => setShowConfirmModal(true)}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </details>
        </div>

        <div className="config-group">
          <details className="config-details">
            <summary className="config-details-summary">
              <div className="header-text-container">
                <h3>Local Market Data Import</h3>
                <p className="config-group-description">
                  Upload a local market data spreadsheet (.xlsx) to update comparison values. All properties will be automatically re-evaluated using the new data.
                </p>
              </div>
              <span className="expand-indicator">▼</span>
            </summary>

            <div className="config-details-content">
              {uploadError && <div className="status-message error">{uploadError}</div>}
              {uploadSuccess && <div className="status-message success">{uploadSuccess}</div>}

              <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''} ${isDragReject ? 'reject' : ''}`}>
                <input {...getInputProps()} />
                <div className="dropzone-content">
                  <span className="dropzone-icon">📁</span>
                  {file ? (
                    <p className="dropzone-file-name">Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)</p>
                  ) : isDragActive ? (
                    <p>Drop the .xlsx file here...</p>
                  ) : (
                    <p>Drag and drop a <strong>.xlsx</strong> spreadsheet here, or click to select a file</p>
                  )}
                </div>
              </div>

              {file && (
                <div className="upload-actions">
                  <button
                    className="btn-upload"
                    onClick={handleUpload}
                    disabled={uploading}
                  >
                    {uploading ? 'Importing & Evaluating...' : 'Start Import'}
                  </button>
                  <button
                    className="btn-cancel-upload"
                    onClick={() => setFile(null)}
                    disabled={uploading}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </details>
        </div>
      </main>

      {showConfirmModal && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal-content">
            <h3>Save Configuration</h3>
            <p>
              Are you sure you want to save the changes made to the condition
              classification configuration?
            </p>
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-confirm"
                onClick={() => {
                  setShowConfirmModal(false);
                  handleSave();
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
